import { Currency, CurrencyAmount, JSBI, Pair, Percent, Token, TokenAmount, Trade, TradeType } from '@uniswap/sdk'
import flatMap from 'lodash.flatmap'
import { useMemo } from 'react'

import { BASES_TO_CHECK_TRADES_AGAINST, CUSTOM_BASES } from '../constants'
import { PairState, usePairs } from '../data/Reserves'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { useSelectedTokenList } from '../state/lists/hooks'
import { useUserAddedTokens } from '../state/user/hooks'

import { useActiveWeb3React } from './index'
import { getTaxAdjustedOutputAmount, getTaxAdjustedInputAmount, isVTToken } from '../utils/taxDetection'

function useAllCommonPairs(currencyA?: Currency, currencyB?: Currency): Pair[] {
  const { chainId } = useActiveWeb3React()
  const tokenList = useSelectedTokenList()

  // 获取基础代币（从常量配置）
  const baseTokens: Token[] = chainId ? BASES_TO_CHECK_TRADES_AGAINST[chainId] : []
  // 获取token list中的所有代币
  const allTokensFromList: Token[] = useMemo(() => {
    if (!chainId) return []
    const chainTokens = tokenList[chainId] || {}
    return Object.values(chainTokens)
  }, [tokenList, chainId])
  // 获取用户添加的代币
  const userAddedTokens = useUserAddedTokens()

  // 合并基础代币、token list中的代币和用户添加的代币，去重
  // 为了性能考虑，限制用于路由的代币数量
  const bases: Token[] = useMemo(() => {
    const allTokens = [...baseTokens]

    // 从token list中添加代币，但限制数量以避免性能问题
    const maxTokensFromList = 50 // 限制最多50个代币用于路由
    let addedCount = 0

    allTokensFromList.forEach(token => {
      if (addedCount >= maxTokensFromList) return
      if (!allTokens.find(t => t.address === token.address)) {
        allTokens.push(token)
        addedCount++
      }
    })

    // 添加用户添加的代币（不限制数量，因为用户添加的代币通常不多）
    userAddedTokens.forEach(token => {
      if (!allTokens.find(t => t.address === token.address)) {
        allTokens.push(token)
      }
    })

    return allTokens
  }, [baseTokens, allTokensFromList, userAddedTokens])

  const [tokenA, tokenB] = chainId
    ? [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)]
    : [undefined, undefined]

  const basePairs: [Token, Token][] = useMemo(
    () =>
      flatMap(bases, (base): [Token, Token][] => bases.map(otherBase => [base, otherBase])).filter(
        ([t0, t1]) => t0.address !== t1.address
      ),
    [bases]
  )

  const allPairCombinations: [Token, Token][] = useMemo(
    () =>
      tokenA && tokenB
        ? [
            // the direct pair
            [tokenA, tokenB],
            // token A against all bases
            ...bases.map((base): [Token, Token] => [tokenA, base]),
            // token B against all bases
            ...bases.map((base): [Token, Token] => [tokenB, base]),
            // each base against all bases
            ...basePairs
          ]
            .filter((tokens): tokens is [Token, Token] => Boolean(tokens[0] && tokens[1]))
            .filter(([t0, t1]) => t0.address !== t1.address)
            .filter(([tokenA, tokenB]) => {
              if (!chainId) return true
              const customBases = CUSTOM_BASES[chainId]
              if (!customBases) return true

              const customBasesA: Token[] | undefined = customBases[tokenA.address]
              const customBasesB: Token[] | undefined = customBases[tokenB.address]

              if (!customBasesA && !customBasesB) return true

              if (customBasesA && !customBasesA.find(base => tokenB.equals(base))) return false
              if (customBasesB && !customBasesB.find(base => tokenA.equals(base))) return false

              return true
            })
        : [],
    [tokenA, tokenB, bases, basePairs, chainId]
  )

  const allPairs = usePairs(allPairCombinations)

  // only pass along valid pairs, non-duplicated pairs
  return useMemo(
    () =>
      Object.values(
        allPairs
          // filter out invalid pairs
          .filter((result): result is [PairState.EXISTS, Pair] => Boolean(result[0] === PairState.EXISTS && result[1]))
          // filter out duplicated pairs
          .reduce<{ [pairAddress: string]: Pair }>((memo, [, curr]) => {
            memo[curr.liquidityToken.address] = memo[curr.liquidityToken.address] ?? curr
            return memo
          }, {})
      ),
    [allPairs]
  )
}

/**
 * Returns the best trade for the exact amount of tokens in to the given token out
 */
// 创建税费感知的 Trade 对象
function createTaxAwareTrade(originalTrade: Trade): Trade {
  return {
    ...originalTrade,
    get outputAmount() {
      try {
        return getTaxAdjustedOutputAmount(originalTrade)
      } catch (error) {
        console.warn('Error calculating tax-adjusted output amount:', error)
        return originalTrade.outputAmount
      }
    },
    get inputAmount() {
      try {
        return getTaxAdjustedInputAmount(originalTrade)
      } catch (error) {
        console.warn('Error calculating tax-adjusted input amount:', error)
        return originalTrade.inputAmount
      }
    },
    // 重写 minimumAmountOut 方法，使用税费调整后的 outputAmount
    minimumAmountOut(slippageTolerance: Percent): CurrencyAmount {
      try {
        if (originalTrade.tradeType === TradeType.EXACT_OUTPUT) {
          return getTaxAdjustedOutputAmount(originalTrade)
        } else {
          // 使用税费调整后的 outputAmount 计算最少收到数量
          const taxAdjustedOutput = getTaxAdjustedOutputAmount(originalTrade)
          const allowedSlippageBips = Math.floor(
            (Number(slippageTolerance.numerator) * 10000) / Number(slippageTolerance.denominator)
          )

          // 安全检查：确保滑点不超过 100%
          const effectiveSlippageBips = Math.min(allowedSlippageBips, 10000)

          const minOutRaw = JSBI.divide(
            JSBI.multiply(taxAdjustedOutput.raw, JSBI.BigInt(10000 - effectiveSlippageBips)),
            JSBI.BigInt(10000)
          )

          return taxAdjustedOutput instanceof TokenAmount
            ? new TokenAmount(taxAdjustedOutput.token, minOutRaw)
            : CurrencyAmount.ether(minOutRaw)
        }
      } catch (error) {
        console.warn('Error calculating minimum amount out:', error)
        return originalTrade.minimumAmountOut(slippageTolerance)
      }
    },
    // 重写 maximumAmountIn 方法，使用税费调整后的 inputAmount
    maximumAmountIn(slippageTolerance: Percent): CurrencyAmount {
      try {
        if (originalTrade.tradeType === TradeType.EXACT_INPUT) {
          return getTaxAdjustedInputAmount(originalTrade)
        } else {
          // 使用税费调整后的 inputAmount 计算最大输入数量
          const taxAdjustedInput = getTaxAdjustedInputAmount(originalTrade)
          const allowedSlippageBips = Math.floor(
            (Number(slippageTolerance.numerator) * 10000) / Number(slippageTolerance.denominator)
          )

          // 安全检查：确保滑点不超过 100%
          const effectiveSlippageBips = Math.min(allowedSlippageBips, 10000)

          const maxInRaw = JSBI.divide(
            JSBI.multiply(taxAdjustedInput.raw, JSBI.BigInt(10000 + effectiveSlippageBips)),
            JSBI.BigInt(10000)
          )

          return taxAdjustedInput instanceof TokenAmount
            ? new TokenAmount(taxAdjustedInput.token, maxInRaw)
            : CurrencyAmount.ether(maxInRaw)
        }
      } catch (error) {
        console.warn('Error calculating maximum amount in:', error)
        return originalTrade.maximumAmountIn(slippageTolerance)
      }
    },
    executionPrice: originalTrade.executionPrice,
    priceImpact: originalTrade.priceImpact,
    nextMidPrice: originalTrade.nextMidPrice
  } as Trade
}

export function useTradeExactIn(currencyAmountIn?: CurrencyAmount, currencyOut?: Currency): Trade | null {
  const allowedPairs = useAllCommonPairs(currencyAmountIn?.currency, currencyOut)
  return useMemo(() => {
    if (currencyAmountIn && currencyOut && allowedPairs.length > 0) {
      const bestTrades = Trade.bestTradeExactIn(allowedPairs, currencyAmountIn, currencyOut, {
        maxHops: 3,
        maxNumResults: 1
      })
      if (bestTrades.length > 0) {
        return createTaxAwareTrade(bestTrades[0])
      }
    }
    return null
  }, [allowedPairs, currencyAmountIn, currencyOut])
}

/**
 * Returns all possible trades for the exact amount of tokens in to the given token out
 */
export function useAllTradesExactIn(currencyAmountIn?: CurrencyAmount, currencyOut?: Currency): Trade[] {
  const allowedPairs = useAllCommonPairs(currencyAmountIn?.currency, currencyOut)
  return useMemo(() => {
    if (currencyAmountIn && currencyOut && allowedPairs.length > 0) {
      const originalTrades = Trade.bestTradeExactIn(allowedPairs, currencyAmountIn, currencyOut, {
        maxHops: 3,
        maxNumResults: 5
      })

      // 过滤掉包含 VT 代币的路由（VT 不支持购买）
      const filteredTrades = originalTrades.filter(trade => {
        // 检查路由中是否包含 VT 代币
        return !trade.route.path.some(token => isVTToken(token.chainId, token.address))
      })

      return filteredTrades.map(trade => createTaxAwareTrade(trade))
    }
    return []
  }, [allowedPairs, currencyAmountIn, currencyOut])
}

/**
 * Returns the best trade for the token in to the exact amount of token out
 */
export function useTradeExactOut(currencyIn?: Currency, currencyAmountOut?: CurrencyAmount): Trade | null {
  const allowedPairs = useAllCommonPairs(currencyIn, currencyAmountOut?.currency)

  return useMemo(() => {
    if (currencyIn && currencyAmountOut && allowedPairs.length > 0) {
      const bestTrades = Trade.bestTradeExactOut(allowedPairs, currencyIn, currencyAmountOut, {
        maxHops: 3,
        maxNumResults: 1
      })
      if (bestTrades.length > 0) {
        return createTaxAwareTrade(bestTrades[0])
      }
    }
    return null
  }, [allowedPairs, currencyIn, currencyAmountOut])
}

/**
 * Returns all possible trades for the token in to the exact amount of token out
 */
export function useAllTradesExactOut(currencyIn?: Currency, currencyAmountOut?: CurrencyAmount): Trade[] {
  const allowedPairs = useAllCommonPairs(currencyIn, currencyAmountOut?.currency)

  return useMemo(() => {
    if (currencyIn && currencyAmountOut && allowedPairs.length > 0) {
      const originalTrades = Trade.bestTradeExactOut(allowedPairs, currencyIn, currencyAmountOut, {
        maxHops: 3,
        maxNumResults: 5
      })

      // 过滤掉包含 VT 代币的路由（VT 不支持购买）
      const filteredTrades = originalTrades.filter(trade => {
        // 检查路由中是否包含 VT 代币
        return !trade.route.path.some(token => isVTToken(token.chainId, token.address))
      })

      return filteredTrades.map(trade => createTaxAwareTrade(trade))
    }
    return []
  }, [allowedPairs, currencyIn, currencyAmountOut])
}
